import { useMemo, useState } from 'react';

import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import {
  ChevronDown,
  ChevronUp,
  Forward,
  MailOpen,
  Paperclip,
  Reply,
  TextAlignStart,
} from 'lucide-react';
import prettyBytes from 'pretty-bytes';

import { gql } from '@graphql/__generated__';
import type { EmailActivityItemContentFragment } from '@graphql/__generated__/graphql';
import { ownMailboxAddresses } from '@modules/_shared/components/email-form/replyRecipients';
import { ghostButtonSx } from '@modules/_shared/components/feed-v2/constants/styles';
import { FileIcon } from '@modules/_shared/components/FileIcon';
import { UnfoldedEmailMessage } from '@modules/_shared/components/UnfoldedEmailMessage';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';
import { shortenFilename } from '@shared/utils/string';

export const EMAIL_ACTIVITY_ITEM_CONTENT_FRAGMENT = gql(/* GraphQL */ `
  fragment EmailActivityItemContent on activities {
    id
    email_message {
      id
      from
      to
      cc
      bcc
      reply_to
      is_sent
      subject
      snippet
      message_id
      created_by
      date
      email_messages_files {
        nylas_file_id
        name
        size
        content_type
      }
    }
  }
`);

type EmailActivityItemContentProps = {
  activity: EmailActivityItemContentFragment;
  onReplyClicked: () => void;
  onForwardClicked: () => void;
};

export const EmailActivityItemContent = ({
  activity,
  onReplyClicked,
  onForwardClicked,
}: EmailActivityItemContentProps) => {
  const [emailUnfolded, setEmailUnfolded] = useState(false);
  const [bodyLoading, setBodyLoading] = useState(false);
  const { t } = useLocale();
  const { me } = useAppData();

  // Can reply if user was involved in the email thread
  const canReply = useMemo(() => {
    if (activity.email_message == null) {
      return false;
    }

    // The connected mailbox address counts too: it can be an alias of the
    // login, never listed in `emails`.
    const userEmails =
      me != null
        ? ownMailboxAddresses(me).map(email => email.toLowerCase())
        : [];

    // Helper to check if user is in recipient list
    const isUserInRecipients = (
      recipients: Array<{ email?: string | null }> | null,
    ) =>
      recipients?.some(
        r => r.email && userEmails.includes(r.email.toLowerCase()),
      ) ?? false;

    return (
      isUserInRecipients(activity.email_message.from) ||
      isUserInRecipients(activity.email_message.to) ||
      isUserInRecipients(activity.email_message.cc) ||
      isUserInRecipients(activity.email_message.bcc)
    );
  }, [activity.email_message, me]);

  if (activity.email_message == null) {
    return null;
  }

  const snippetPreview = (
    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        wordBreak: 'break-word',
      }}
    >
      {activity.email_message.snippet ?? t('No snippet')}
    </Typography>
  );

  return (
    <Box sx={{ mt: 1.5 }}>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: 'flex-start',
          mb: 1.5,
        }}
      >
        <TextAlignStart
          size={16}
          color={customPalette.gray600}
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, color: 'text.primary' }}
        >
          {activity.email_message.subject ?? t('No subject')}
        </Typography>
      </Stack>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: 'flex-start',
        }}
      >
        <MailOpen
          size={16}
          color={customPalette.gray600}
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        <Box
          sx={{
            flex: 1,
            bgcolor: 'grey.100',
            borderRadius: '6px',
            p: 1,
          }}
        >
          {emailUnfolded ? (
            <Box
              sx={{
                typography: 'caption',
                color: 'text.secondary',
              }}
            >
              <UnfoldedEmailMessage
                emailMessageId={activity.email_message.message_id ?? ''}
                createdBy={activity.email_message.created_by ?? ''}
                onLoadingChange={setBodyLoading}
                fallback={snippetPreview}
              />
            </Box>
          ) : (
            snippetPreview
          )}
          {activity.email_message.message_id != null &&
            activity.email_message.created_by != null && (
              <Button
                size="small"
                disableRipple
                loading={bodyLoading}
                loadingPosition="end"
                onClick={() => setEmailUnfolded(!emailUnfolded)}
                endIcon={
                  emailUnfolded ? (
                    <ChevronUp size={14} />
                  ) : (
                    <ChevronDown size={14} />
                  )
                }
                sx={{ mt: 1, ...ghostButtonSx }}
              >
                {emailUnfolded && !bodyLoading
                  ? t('Show less')
                  : t('Show more')}
              </Button>
            )}
        </Box>
      </Stack>
      {activity.email_message.email_messages_files.length > 0 && (
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: 'flex-start',
            mt: 1.5,
          }}
        >
          <Paperclip
            size={16}
            color={customPalette.gray600}
            style={{ marginTop: 4, flexShrink: 0 }}
          />
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: 'wrap',
              mb: '-8px !important',
            }}
          >
            {activity.email_message.email_messages_files.map(file => (
              <Chip
                key={file.nylas_file_id}
                size="small"
                icon={
                  <FileIcon mimetype={file.content_type ?? null} size={16} />
                }
                label={`${shortenFilename(file.name ?? '')} (${prettyBytes(
                  file.size ?? 0,
                )})`}
                sx={{ mb: `8px !important` }}
              />
            ))}
          </Stack>
        </Stack>
      )}
      {canReply && (
        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: 'flex-end',
            mt: 1.5,
          }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<Reply size={16} />}
            onClick={onReplyClicked}
          >
            {t('reply')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Forward size={16} />}
            onClick={onForwardClicked}
          >
            {t('forward')}
          </Button>
        </Stack>
      )}
    </Box>
  );
};
